// API文档页面 - 完整的现代化接口文档
import React, { useState, useEffect } from 'react';
import { Copy, Check, Book, Key, Code, Database, Shield, Zap, Layers, FileText, Image, Mic, Video, Brain, Search } from 'lucide-react';
import Navbar from '../components/Navbar';

const Documentation: React.FC = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 导航菜单结构
  const navigationSections = [
    {
      title: 'Getting Started',
      items: [
        { id: 'introduction', label: 'Introduction', icon: Book },
        { id: 'authentication', label: 'Authentication', icon: Key },
        { id: 'quickstart', label: 'Quick Start', icon: Zap },
        { id: 'errors', label: 'Error Handling', icon: Shield }
      ]
    },
    {
      title: 'Core API',
      items: [
        { id: 'models', label: 'List Models', icon: Database },
        { id: 'text-generation', label: 'Text Generation', icon: FileText },
        { id: 'vision', label: 'Vision Analysis', icon: Image },
        { id: 'code-generation', label: 'Code Generation', icon: Code }
      ]
    },
    {
      title: 'AI Capabilities',
      items: [
        { id: 'image-generation', label: 'Image Generation', icon: Image },
        { id: 'speech-recognition', label: 'Speech Recognition', icon: Mic },
        { id: 'tts', label: 'Text-to-Speech', icon: Mic },
        { id: 'video-generation', label: 'Video Generation', icon: Video },
        { id: '3d-generation', label: '3D Generation', icon: Layers },
        { id: 'embeddings', label: 'Embeddings', icon: Brain },
        { id: 'moderation', label: 'Content Moderation', icon: Shield }
      ]
    },

    {
      title: 'Advanced',
      items: [
        { id: 'async-tasks', label: 'Async Tasks', icon: Zap },
        { id: 'rate-limits', label: 'Rate Limits', icon: Shield },
        { id: 'sdk-compatibility', label: 'SDK Compatibility', icon: Code }
      ]
    }
  ];

  // 复制代码功能
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // 代码示例组件
  const CodeBlock: React.FC<{ code: string; language: string; title: string; id: string }> =
    ({ code, language, title, id }) => (
      <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800 border-b border-gray-700">
          <span className="text-xs font-medium text-gray-300">{title}</span>
          <button
            onClick={() => copyToClipboard(code, id)}
            className="flex items-center space-x-1 text-gray-400 hover:text-gray-200 transition-colors"
          >
            {copiedCode === id ? (
              <>
                <Check className="h-3 w-3" />
                <span className="text-xs">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                <span className="text-xs">Copy</span>
              </>
            )}
          </button>
        </div>
        <pre className="p-3 text-xs text-gray-100 overflow-x-auto">
          <code className={`language-${language}`}>{code}</code>
        </pre>
      </div>
    );

  // JSON 响应组件
  const JsonResponse: React.FC<{ data: any; title: string; id: string }> =
    ({ data, title, id }) => (
      <div className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-100 border-b border-slate-200">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-xs font-medium text-slate-700">{title}</span>
          </div>
          <button
            onClick={() => copyToClipboard(JSON.stringify(data, null, 2), id)}
            className="flex items-center space-x-1 text-slate-500 hover:text-slate-700 transition-colors"
          >
            {copiedCode === id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </button>
        </div>
        <pre className="p-3 text-xs text-slate-800 overflow-x-auto">
          <code>{JSON.stringify(data, null, 2)}</code>
        </pre>
      </div>
    );

  // API 端点组件
  const ApiEndpoint: React.FC<{ method: string; endpoint: string; description: string }> =
    ({ method, endpoint, description }) => (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
        <div className="flex items-center space-x-2 mb-1">
          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${method === 'GET' ? 'bg-green-100 text-green-800' :
            method === 'POST' ? 'bg-blue-100 text-blue-800' :
              method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
            }`}>
            {method}
          </span>
          <code className="text-xs font-mono text-blue-800 bg-blue-100 px-1.5 py-0.5 rounded">
            {endpoint}
          </code>
        </div>
        <p className="text-xs text-blue-700">{description}</p>
      </div>
    );

  // 内容渲染函数
  const renderContent = () => {
    switch (activeSection) {
      case 'introduction':
        return (
          <div className="max-w-4xl">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">ModelPool API Documentation</h1>
            <p className="text-base text-gray-600 mb-6">
              Unified API for 113+ AI models across 12 categories. Build powerful AI applications with a single, consistent interface.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Base URL</h3>
                <code className="text-xs font-mono text-blue-800 bg-blue-100 px-2 py-1 rounded-lg block">
                  https://p.qmtju.com
                </code>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-purple-900 mb-2">API Version</h3>
                <code className="text-xs font-mono text-purple-800 bg-purple-100 px-2 py-1 rounded-lg block">
                  v1.7
                </code>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Platform Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Brain className="h-4 w-4 text-blue-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">113+ Models</h3>
                  <p className="text-xs text-gray-600">Access the latest AI models from leading providers</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Layers className="h-4 w-4 text-green-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">12 Categories</h3>
                  <p className="text-xs text-gray-600">Text, Image, Audio, Video, 3D, Code and more</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Code className="h-4 w-4 text-purple-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">SDK Compatible</h3>
                  <p className="text-xs text-gray-600">Works with OpenAI SDK and standard libraries</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Shield className="h-4 w-4 text-orange-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Enterprise Ready</h3>
                  <p className="text-xs text-gray-600">Rate limiting, usage tracking, and secure billing</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-yellow-900 mb-2">🚀 Key Features</h3>
              <ul className="space-y-1 text-yellow-800 text-xs">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  Unified API across 113+ AI models from various providers
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  OpenAI SDK compatible endpoints for easy migration
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  Pay-per-use billing with real-time cost tracking
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  Both synchronous and asynchronous processing
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  Enterprise-grade security and rate limiting
                </li>
              </ul>
            </div>
          </div>
        );

      case 'authentication':
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Authentication</h1>
            <p className="text-xl text-gray-600 mb-8">
              Secure your API requests with API keys. All requests require authentication.
            </p>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">API Key Format</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Production Keys</h3>
                    <code className="text-sm font-mono text-blue-800 bg-blue-100 px-3 py-2 rounded block">
                      mp_live_xxxxxxxxxxxxxxxxxxxx
                    </code>
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Test Keys</h3>
                    <code className="text-sm font-mono text-blue-800 bg-blue-100 px-3 py-2 rounded block">
                      mp_test_xxxxxxxxxxxxxxxxxxxx
                    </code>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Header</h3>
              <CodeBlock
                title="Authorization Header"
                language="bash"
                id="auth-header"
                code="x-api-key: mp_live_your_api_key_here"
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Example</h2>
              <CodeBlock
                title="cURL Example"
                language="bash"
                id="auth-example"
                code={`curl -X GET "https://p.qmtju.com/v1/models" \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json"`}
              />
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">🔐 Security Features</h3>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Enhanced API key format with built-in checksum validation
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Environment isolation between test and production keys
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Multi-layer rate limiting protection
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Usage tracking and audit logs
                </li>
              </ul>
            </div>
          </div>
        );

      case 'quickstart':
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Quick Start</h1>
            <p className="text-xl text-gray-600 mb-8">
              Get started with ModelPool API in minutes. Follow these simple steps to make your first AI model call.
            </p>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">4 Steps to Get Started</h2>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Get Your API Key</h3>
                    <p className="text-gray-700 mb-4">Sign up at ModelPool and get your API key from the dashboard.</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <code className="text-sm text-gray-800">mp_live_your_api_key_here</code>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Choose Your Method</h3>
                    <p className="text-gray-700 mb-4">Use cURL, OpenAI SDK, or any HTTP client.</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                        <Code className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                        <p className="text-sm font-medium">cURL</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                        <FileText className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                        <p className="text-sm font-medium">OpenAI SDK</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                        <Zap className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                        <p className="text-sm font-medium">HTTP Client</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Make Your First Request</h3>
                    <p className="text-gray-700 mb-4">Try a simple text generation call:</p>

                    <CodeBlock
                      title="First API Call"
                      language="bash"
                      id="first-call"
                      code={`curl -X POST https://p.qmtju.com/v1/text/chat/completions \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "DeepSeek-V3",
    "messages": [
      {"role": "user", "content": "Hello! How are you?"}
    ],
    "max_tokens": 100
  }'`}
                    />
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    4
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Get Your Response</h3>
                    <p className="text-gray-700 mb-4">You'll receive a JSON response with the AI-generated content:</p>

                    <JsonResponse
                      title="Response"
                      id="first-response"
                      data={{
                        "id": "chatcmpl-abc123",
                        "object": "chat.completion",
                        "model": "DeepSeek-V3",
                        "choices": [
                          {
                            "index": 0,
                            "message": {
                              "role": "assistant",
                              "content": "Hello! I'm doing well, thank you for asking. How can I assist you today?"
                            },
                            "finish_reason": "stop"
                          }
                        ],
                        "usage": {
                          "prompt_tokens": 12,
                          "completion_tokens": 20,
                          "total_tokens": 32
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Using OpenAI SDK</h2>
              <p className="text-gray-600 mb-4">
                If you're already using OpenAI SDK, just change the base URL:
              </p>

              <CodeBlock
                title="JavaScript/TypeScript"
                language="javascript"
                id="openai-sdk"
                code={`import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "https://p.qmtju.com/v1",
  apiKey: "mp_live_your_api_key_here",
});

const response = await client.chat.completions.create({
  model: "DeepSeek-V3",
  messages: [
    {"role": "user", "content": "Hello!"}
  ],
});

// Process the response`}
              />

              <CodeBlock
                title="Python"
                language="python"
                id="python-sdk"
                code={`import openai

client = openai.OpenAI(
    base_url="https://p.qmtju.com/v1",
    api_key="mp_live_your_api_key_here"
)

response = client.chat.completions.create(
    model="DeepSeek-V3",
    messages=[
        {"role": "user", "content": "Hello!"}
    ]
)

print(response.choices[0].message.content)`}
              />
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">🎉 Next Steps</h3>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Explore different AI models for text, images, audio, and video
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Check out async endpoints for long-running tasks
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Implement error handling and rate limiting
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Monitor your usage and costs in the dashboard
                </li>
              </ul>
            </div>
          </div>
        );

      case 'errors':
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Error Handling</h1>
            <p className="text-xl text-gray-600 mb-8">
              Learn how to handle errors gracefully and implement robust error handling in your applications.
            </p>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Response Format</h2>
              <p className="text-gray-600 mb-4">
                All errors follow a consistent JSON structure:
              </p>

              <JsonResponse
                title="Error Response Structure"
                id="error-structure"
                data={{
                  "error": {
                    "type": "invalid_request_error",
                    "message": "The model 'gpt-99' does not exist"
                  }
                }}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Common HTTP Status Codes</h2>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status Code</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Error Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">400</td>
                      <td className="px-4 py-3 text-sm text-gray-600">invalid_request_error</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Invalid request parameters</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">401</td>
                      <td className="px-4 py-3 text-sm text-gray-600">authentication_error</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Invalid or missing API key</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">402</td>
                      <td className="px-4 py-3 text-sm text-gray-600">insufficient_funds</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Insufficient account balance</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">403</td>
                      <td className="px-4 py-3 text-sm text-gray-600">model_access_denied</td>
                      <td className="px-4 py-3 text-sm text-gray-600">No access to requested model</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">429</td>
                      <td className="px-4 py-3 text-sm text-gray-600">rate_limit_exceeded</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Rate limit exceeded</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">500</td>
                      <td className="px-4 py-3 text-sm text-gray-600">internal_error</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Server error</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Handling Examples</h2>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">JavaScript/TypeScript</h3>
              <CodeBlock
                title="Error Handling with Fetch"
                language="javascript"
                id="js-error-handling"
                code={`async function callModelPool(prompt) {
  try {
    const response = await fetch('https://p.qmtju.com/v1/text/chat/completions', {
      method: 'POST',
      headers: {
        'x-api-key': 'mp_live_your_api_key_here',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'DeepSeek-V3',
        messages: [{ role: 'user', content: prompt }]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      
      switch (response.status) {
        case 401:
          throw new Error('Invalid API key');
        case 402:
          throw new Error('Insufficient funds - please add credits');
        case 429:
          throw new Error('Rate limit exceeded - please wait before retrying');
        case 500:
          throw new Error('Server error - please try again later');
        default:
          throw new Error(error.error?.message || 'API request failed');
      }
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
}`}
              />

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Python</h3>
              <CodeBlock
                title="Error Handling with Requests"
                language="python"
                id="python-error-handling"
                code={`import requests
import time

def call_modelpool(prompt, max_retries=3):
    for attempt in range(max_retries):
        try:
            response = requests.post(
                'https://p.qmtju.com/v1/text/chat/completions',
                headers={
                    'x-api-key': 'mp_live_your_api_key_here',
                    'Content-Type': 'application/json'
                },
                json={
                    'model': 'DeepSeek-V3',
                    'messages': [{'role': 'user', 'content': prompt}]
                }
            )
            
            if response.status_code == 200:
                return response.json()
            
            elif response.status_code == 401:
                raise Exception('Invalid API key')
            
            elif response.status_code == 402:
                raise Exception('Insufficient funds - please add credits')
            
            elif response.status_code == 429:
                if attempt < max_retries - 1:
                    wait_time = 2 ** attempt  # Exponential backoff
                    print(f'Rate limited, waiting {wait_time}s...')
                    time.sleep(wait_time)
                    continue
                else:
                    raise Exception('Rate limit exceeded after retries')
            
            elif response.status_code == 500:
                if attempt < max_retries - 1:
                    print(f'Server error, retrying... (attempt {attempt + 1})')
                    time.sleep(1)
                    continue
                else:
                    raise Exception('Server error after retries')
            
            else:
                error_data = response.json()
                raise Exception(error_data.get('error', {}).get('message', 'Unknown error'))
                
        except requests.RequestException as e:
            if attempt < max_retries - 1:
                time.sleep(1)
                continue
            else:
                raise Exception(f'Network error: {str(e)}')`}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Rate Limit Error Handling</h2>
              <p className="text-gray-600 mb-4">
                When you hit rate limits, the API returns a 429 status with retry information:
              </p>

              <JsonResponse
                title="Rate Limit Error"
                id="rate-limit-error"
                data={{
                  "error": {
                    "type": "rate_limit_exceeded",
                    "message": "IP rate limit exceeded. Please try again later.",
                    "retryAfter": 45
                  }
                }}
              />

              <p className="text-gray-600 mb-4">
                Implement exponential backoff for rate limit errors:
              </p>

              <CodeBlock
                title="Exponential Backoff"
                language="javascript"
                id="exponential-backoff"
                code={`async function callWithBackoff(apiCall, maxRetries = 3) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      if (error.status === 429 && attempt < maxRetries - 1) {
        const waitTime = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        // Rate limited, waiting
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      throw error;
    }
  }
}`}
              />
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-orange-900 mb-3">💡 Best Practices</h3>
              <ul className="space-y-2 text-orange-800">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Always implement retry logic for rate limits and server errors
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Use exponential backoff to avoid overwhelming the API
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Monitor your account balance to avoid insufficient funds errors
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Log errors for debugging and monitoring purposes
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Provide meaningful error messages to your users
                </li>
              </ul>
            </div>
          </div>
        );

      case 'models':
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">List Models</h1>
            <p className="text-xl text-gray-600 mb-8">
              Retrieve all available AI models organized by category with pricing information.
            </p>

            <ApiEndpoint
              method="GET"
              endpoint="/v1/models"
              description="Returns all available models grouped by category with detailed information"
            />

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Request</h2>
              <CodeBlock
                title="cURL"
                language="bash"
                id="models-request"
                code={`curl https://p.qmtju.com/v1/models \\
  -H "x-api-key: mp_live_your_api_key_here"`}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Response</h2>
              <JsonResponse
                title="200"
                id="models-response"
                data={{
                  "object": "list",
                  "data": [
                    {
                      "id": 1,
                      "name": "Text Generation",
                      "slug": "text-generation",
                      "description": "Advanced language models for text generation, chat, and completion",
                      "api_prefix": "/v1/text",
                      "models": [
                        {
                          "id": 1,
                          "name": "DeepSeek-V3",
                          "slug": "DeepSeek-V3",
                          "provider_model_name": "deepseek-chat",
                          "description": "Latest generation reasoning model with advanced capabilities",
                          "status": "active",
                          "parameters": {
                            "max_tokens": 8192,
                            "supports_streaming": true,
                            "context_length": 64000
                          },
                          "tags": ["text", "chat", "reasoning"],
                          "pricing": {
                            "display_price": "$1.50 / 1M input tokens, $3.00 / 1M output tokens",
                            "pricing_type": "token",
                            "description": "Pay per token usage"
                          }
                        }
                      ]
                    }
                  ]
                }}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">📊 Model Categories</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Text Generation:</strong> 30+ models</p>
                  <p><strong>Image Generation:</strong> 20+ models</p>
                  <p><strong>Code Generation:</strong> 5+ models</p>
                  <p><strong>Speech Recognition:</strong> 4+ models</p>
                  <p><strong>Text-to-Speech:</strong> 10+ models</p>
                  <p><strong>Vision Analysis:</strong> 9+ models</p>
                </div>
                <div>
                  <p><strong>Video Generation:</strong> 6+ models</p>
                  <p><strong>3D Generation:</strong> 2+ models</p>
                  <p><strong>Embeddings:</strong> 17+ models</p>
                  <p><strong>Content Moderation:</strong> 2+ models</p>
                  <p><strong>Industry Models:</strong> 7+ models</p>
                  <p><strong>Document Processing:</strong> 1+ models</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'text-generation':
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Text Generation</h1>
            <p className="text-xl text-gray-600 mb-8">
              Generate text using advanced language models. Compatible with OpenAI Chat Completions API.
            </p>

            <ApiEndpoint
              method="POST"
              endpoint="/v1/text/chat/completions"
              description="Generate text completions using chat-based language models"
            />

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Popular Models</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">DeepSeek-V3</h3>
                  <p className="text-sm text-gray-600 mb-2">Latest generation reasoning model</p>
                  
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">GLM-4_5</h3>
                  <p className="text-sm text-gray-600 mb-2">Intelligent agent-optimized model</p>
                 
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Request</h2>
              <CodeBlock
                title="cURL"
                language="bash"
                id="text-request"
                code={`curl -X POST https://p.qmtju.com/v1/text/chat/completions \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "DeepSeek-V3",
    "messages": [
      {"role": "user", "content": "Explain quantum computing in simple terms"}
    ],
    "max_tokens": 500,
    "temperature": 0.7,
    "stream": false
  }'`}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Response</h2>
              <JsonResponse
                title="200"
                id="text-response"
                data={{
                  "id": "chatcmpl-abc123",
                  "object": "chat.completion",
                  "created": 1692901234,
                  "model": "DeepSeek-V3",
                  "choices": [
                    {
                      "index": 0,
                      "message": {
                        "role": "assistant",
                        "content": "Quantum computing is a revolutionary technology that uses the principles of quantum mechanics..."
                      },
                      "finish_reason": "stop"
                    }
                  ],
                  "usage": {
                    "prompt_tokens": 12,
                    "completion_tokens": 45,
                    "total_tokens": 57
                  }
                }}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Parameters</h2>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                      <td className="px-4 py-3 text-sm text-gray-600">string</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Model identifier (e.g., "DeepSeek-V3")</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">messages</td>
                      <td className="px-4 py-3 text-sm text-gray-600">array</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Array of message objects with role and content</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">max_tokens</td>
                      <td className="px-4 py-3 text-sm text-gray-600">integer</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Maximum tokens to generate (optional)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">temperature</td>
                      <td className="px-4 py-3 text-sm text-gray-600">number</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Sampling temperature between 0 and 2 (optional)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">stream</td>
                      <td className="px-4 py-3 text-sm text-gray-600">boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Enable streaming responses (optional)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'vision':
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Vision Analysis</h1>
            <p className="text-xl text-gray-600 mb-8">
              Analyze images, extract text, identify objects, and understand visual content using advanced vision models.
            </p>

            <ApiEndpoint
              method="POST"
              endpoint="/v1/vision/analyze"
              description="Analyze images and extract information using vision-language models"
            />


            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyze Image from URL</h2>
              <CodeBlock
                title="Image URL Analysis"
                language="bash"
                id="vision-url"
                code={`curl -X POST https://p.qmtju.com/v1/vision/analyze \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "step3",
    "messages": [
      {
        "role": "user",
        "content": [
          {"type": "text", "text": "What do you see in this image? Describe it in detail."},
          {
            "type": "image_url",
            "image_url": {
              "url": "https://example.com/image.jpg"
            }
          }
        ]
      }
    ],
    "max_tokens": 300
  }'`}
              />

              <JsonResponse
                title="Vision Analysis Response"
                id="vision-response"
                data={{
                  "id": "vision-abc123",
                  "object": "chat.completion",
                  "model": "step3",
                  "choices": [
                    {
                      "index": 0,
                      "message": {
                        "role": "assistant",
                        "content": "This image shows a beautiful sunset over a mountain landscape. In the foreground, there are rolling hills covered with green vegetation. The mountains in the background have a purple-blue hue due to atmospheric perspective. The sky displays vibrant colors ranging from deep orange near the horizon to lighter yellow and pink tones higher up. There are some scattered clouds that catch the warm light from the setting sun..."
                      },
                      "finish_reason": "stop"
                    }
                  ],
                  "usage": {
                    "prompt_tokens": 20,
                    "completion_tokens": 95,
                    "total_tokens": 115
                  }
                }}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyze Image from Base64</h2>
              <CodeBlock
                title="Base64 Image Analysis"
                language="bash"
                id="vision-base64"
                code={`curl -X POST https://p.qmtju.com/v1/vision/analyze \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "Qwen2.5-VL-32B-Instruct",
    "messages": [
      {
        "role": "user",
        "content": [
          {"type": "text", "text": "Extract all text from this image and format it nicely."},
          {
            "type": "image_url",
            "image_url": {
              "url": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD..."
            }
          }
        ]
      }
    ],
    "max_tokens": 500
  }'`}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Multiple Images Analysis</h2>
              <CodeBlock
                title="Multi-Image Analysis"
                language="bash"
                id="vision-multi"
                code={`curl -X POST https://p.qmtju.com/v1/vision/analyze \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "InternVL3-78B",
    "messages": [
      {
        "role": "user",
        "content": [
          {"type": "text", "text": "Compare these two images and tell me what differences you notice."},
          {
            "type": "image_url",
            "image_url": {"url": "https://example.com/image1.jpg"}
          },
          {
            "type": "image_url", 
            "image_url": {"url": "https://example.com/image2.jpg"}
          }
        ]
      }
    ],
    "max_tokens": 400
  }'`}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Common Use Cases</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">📄 OCR & Text Extraction</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Extract text from documents</li>
                    <li>• Read handwritten notes</li>
                    <li>• Process receipts and invoices</li>
                    <li>• Digitize printed materials</li>
                  </ul>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">🔍 Object Detection</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Identify objects in images</li>
                    <li>• Count items and people</li>
                    <li>• Detect specific brands or logos</li>
                    <li>• Analyze product catalogs</li>
                  </ul>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">📊 Chart & Graph Analysis</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Extract data from charts</li>
                    <li>• Interpret graphs and plots</li>
                    <li>• Read table contents</li>
                    <li>• Analyze infographics</li>
                  </ul>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">🎨 Content Analysis</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Describe image content</li>
                    <li>• Identify artistic styles</li>
                    <li>• Analyze compositions</li>
                    <li>• Verify image authenticity</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">💡 Vision Analysis Tips</h3>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Use high-resolution images for better text extraction
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Specify exactly what you want to extract or analyze
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  For complex images, break down your questions
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Use appropriate models based on image complexity
                </li>
              </ul>
            </div>
          </div>
        );

      case 'code-generation':
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Code Generation</h1>
            <p className="text-xl text-gray-600 mb-8">
              Generate, complete, and explain code using specialized programming models. Perfect for coding assistants and automation.
            </p>

            <ApiEndpoint
              method="POST"
              endpoint="/v1/code/completions"
              description="Generate code completions using specialized coding models"
            />


            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Generate Code</h2>
              <CodeBlock
                title="Code Generation Request"
                language="bash"
                id="code-generation"
                code={`curl -X POST https://p.qmtju.com/v1/code/completions \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "qwen3-coder-480b-a35b-instruct",
    "messages": [
      {"role": "user", "content": "Write a Python function to calculate the Fibonacci sequence using dynamic programming"}
    ],
    "max_tokens": 300,
    "temperature": 0.2
  }'`}
              />

              <JsonResponse
                title="Code Generation Response"
                id="code-response"
                data={{
                  "id": "codecmpl-abc123",
                  "object": "chat.completion",
                  "model": "qwen3-coder-480b-a35b-instruct",
                  "choices": [
                    {
                      "index": 0,
                      "message": {
                        "role": "assistant",
                        "content": "```python\ndef fibonacci_dp(n):\n    \"\"\"\n    Calculate Fibonacci sequence using dynamic programming\n    \n    Args:\n        n (int): Position in Fibonacci sequence\n    \n    Returns:\n        int: Fibonacci number at position n\n    \"\"\"\n    if n <= 1:\n        return n\n    \n    # Initialize dp array\n    dp = [0] * (n + 1)\n    dp[0] = 0\n    dp[1] = 1\n    \n    # Fill dp array using bottom-up approach\n    for i in range(2, n + 1):\n        dp[i] = dp[i-1] + dp[i-2]\n    \n    return dp[n]\n\n# Example usage\nprint(fibonacci_dp(10))  # Output: 55\n```"
                      },
                      "finish_reason": "stop"
                    }
                  ],
                  "usage": {
                    "prompt_tokens": 25,
                    "completion_tokens": 180,
                    "total_tokens": 205
                  }
                }}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Code Explanation</h2>
              <CodeBlock
                title="Explain Code"
                language="bash"
                id="code-explain"
                code={`curl -X POST https://p.qmtju.com/v1/code/completions \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "deepseek-coder-33B-instruct",
    "messages": [
      {"role": "user", "content": "Explain this code step by step:\\n\\n\`\`\`python\\ndef quicksort(arr):\\n    if len(arr) <= 1:\\n        return arr\\n    pivot = arr[len(arr) // 2]\\n    left = [x for x in arr if x < pivot]\\n    middle = [x for x in arr if x == pivot]\\n    right = [x for x in arr if x > pivot]\\n    return quicksort(left) + middle + quicksort(right)\\n\`\`\`"}
    ],
    "max_tokens": 400,
    "temperature": 0.3
  }'`}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Code Completion</h2>
              <CodeBlock
                title="Complete Code"
                language="bash"
                id="code-completion"
                code={`curl -X POST https://p.qmtju.com/v1/code/completions \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "qwen3-coder-480b-a35b-instruct",
    "messages": [
      {"role": "user", "content": "Complete this JavaScript function:\\n\\nfunction debounce(func, delay) {\\n    let timeoutId;\\n    return function(...args) {\\n        // TODO: Implement debounce logic"}
    ],
    "max_tokens": 200,
    "temperature": 0.1
  }'`}
              />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Multi-Language Support</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">Python</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">JavaScript</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">Java</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">C++</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">Go</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">Rust</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">TypeScript</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <Code className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-medium">PHP</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-3">🚀 Code Generation Features</h3>
              <ul className="space-y-2 text-purple-800">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Function and class generation from descriptions
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Code completion and autocomplete
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Bug fixing and code optimization
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Code explanation and documentation
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Language translation between programming languages
                </li>
              </ul>
            </div>
          </div>
        );

      case 'image-generation':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Image Generation</h1>
      <p className="text-xl text-gray-600 mb-8">
        Create stunning images from text descriptions using state-of-the-art generative models.
      </p>

      <ApiEndpoint
        method="POST"
        endpoint="/v1/images/generations"
        description="Generate images from text prompts using advanced AI models"
      />


      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Basic Image Generation</h2>
        <CodeBlock
          title="Generate Image"
          language="bash"
          id="image-basic"
          code={`curl -X POST https://p.qmtju.com/v1/images/generations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "flux-1-schnell",
    "prompt": "A majestic mountain landscape at sunset with a crystal clear lake reflecting the orange and pink sky, surrounded by pine trees",
    "size": "1024x1024",
    "quality": "standard",
    "n": 1,
    "style": "natural"
  }'`}
        />

        <JsonResponse
          title="Image Generation Response"
          id="image-response"
          data={{
            "created": 1692901234,
            "data": [
              {
                "url": "https://platform-storage.com/images/generated-image-123.png",
                "revised_prompt": "A majestic mountain landscape at sunset with a crystal clear lake reflecting the orange and pink sky, surrounded by pine trees, digital art style"
              }
            ]
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Advanced Parameters</h2>
        <CodeBlock
          title="Advanced Image Generation"
          language="bash"
          id="image-advanced"
          code={`curl -X POST https://p.qmtju.com/v1/images/generations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "stable-diffusion-3",
    "prompt": "Portrait of a cyberpunk character with neon lighting, highly detailed, 8k resolution",
    "negative_prompt": "blurry, low quality, distorted, ugly",
    "size": "1024x1024",
    "steps": 50,
    "guidance_scale": 7.5,
    "seed": 12345,
    "n": 2
  }'`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Batch Generation</h2>
        <CodeBlock
          title="Generate Multiple Images"
          language="bash"
          id="image-batch"
          code={`curl -X POST https://p.qmtju.com/v1/images/generations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "Kolors",
    "prompt": "A cute cartoon character in different poses and expressions",
    "size": "512x512",
    "n": 4,
    "quality": "standard"
  }'`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Image Matting (Background Removal)</h2>
        <p className="text-gray-600 mb-4">
          Remove backgrounds from images and extract the main subject with AI-powered precision.
        </p>

        <ApiEndpoint
          method="POST"
          endpoint="/v1/images/mattings"
          description="Remove background from images using advanced AI models"
        />

        <CodeBlock
          title="Remove Image Background"
          language="bash"
          id="image-matting"
          code={`curl -X POST https://p.qmtju.com/v1/images/mattings \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "model=RMBG-2.0" \\
  -F "image=@input_image.jpg"`}
        />

        <JsonResponse
          title="Image Matting Response"
          id="image-matting-response"
          data={{
            "created": 1692901234,
            "data": [
              {
                "url": "https://platform-storage.com/images/matting-result-123.png",
                "type": "matting_result"
              }
            ]
          }}
        />

        <div className="mt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Advanced Matting</h3>
          <CodeBlock
            title="Matting with Custom Parameters"
            language="bash"
            id="image-matting-advanced"
            code={`curl -X POST https://p.qmtju.com/v1/images/mattings \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "model=RMBG-2.0" \\
  -F "image=@portrait.jpg" \\
  -F "output_format=png" \\
  -F "return_mask=true"`}
          />
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Supported Parameters</h3>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                  <td className="px-4 py-3 text-sm text-gray-600">string</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Background removal model (e.g., "RMBG-2.0")</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">image</td>
                  <td className="px-4 py-3 text-sm text-gray-600">file</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Input image file to process</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">output_format</td>
                  <td className="px-4 py-3 text-sm text-gray-600">string</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Output format: "png" (default), "jpg"</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">return_mask</td>
                  <td className="px-4 py-3 text-sm text-gray-600">boolean</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Whether to return the alpha mask separately</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Supported Parameters</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Image generation model to use</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">prompt</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Text description of the desired image</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">size</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Image dimensions (e.g., "1024x1024", "512x768")</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">n</td>
                <td className="px-4 py-3 text-sm text-gray-600">integer</td>
                <td className="px-4 py-3 text-sm text-gray-600">Number of images to generate (1-10)</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">quality</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">"standard" or "hd" quality</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">style</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">"natural", "vivid", "anime", "photographic"</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Supported Sizes</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <code className="text-sm font-mono">512x512</code>
            <p className="text-xs text-gray-600 mt-1">Square</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <code className="text-sm font-mono">1024x1024</code>
            <p className="text-xs text-gray-600 mt-1">Large Square</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <code className="text-sm font-mono">768x1024</code>
            <p className="text-xs text-gray-600 mt-1">Portrait</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <code className="text-sm font-mono">1024x768</code>
            <p className="text-xs text-gray-600 mt-1">Landscape</p>
          </div>
        </div>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-orange-900 mb-3">🎨 Image Generation Tips</h3>
        <ul className="space-y-2 text-orange-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Be specific and descriptive in your prompts for better results
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use negative prompts to avoid unwanted elements
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Experiment with different models for varying artistic styles
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Higher guidance scales create images closer to your prompt
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use seeds for reproducible generation
          </li>
        </ul>
      </div>
    </div>
  );

  case 'speech-recognition':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Speech Recognition</h1>
      <p className="text-xl text-gray-600 mb-8">
        Convert audio files to text with high accuracy using advanced speech recognition models.
      </p>

      <ApiEndpoint
        method="POST"
        endpoint="/v1/speech/transcriptions"
        description="Transcribe audio files to text using speech recognition models"
      />


      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Basic Transcription</h2>
        <CodeBlock
          title="Transcribe Audio File"
          language="bash"
          id="speech-basic"
          code={`curl -X POST https://p.qmtju.com/v1/speech/transcriptions \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "file=@audio.mp3" \\
  -F "model=whisper-large-v3-turbo" \\
  -F "language=en" \\
  -F "response_format=json"`}
        />

        <JsonResponse
          title="Transcription Response"
          id="speech-response"
          data={{
            "text": "Hello, this is a sample audio transcription. The speech recognition model has accurately converted the spoken words into text format."
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Detailed Transcription</h2>
        <CodeBlock
          title="Detailed Response Format"
          language="bash"
          id="speech-detailed"
          code={`curl -X POST https://p.qmtju.com/v1/speech/transcriptions \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "file=@meeting.wav" \\
  -F "model=whisper-large" \\
  -F "response_format=verbose_json" \\
  -F "timestamp_granularities[]=word" \\
  -F "timestamp_granularities[]=segment"`}
        />

        <JsonResponse
          title="Detailed Transcription Response"
          id="speech-detailed-response"
          data={{
            "task": "transcribe",
            "language": "english",
            "duration": 18.5,
            "text": "Welcome to our team meeting. Today we'll discuss the quarterly results and upcoming projects.",
            "words": [
              {
                "word": "Welcome",
                "start": 0.5,
                "end": 1.0
              },
              {
                "word": "to",
                "start": 1.0,
                "end": 1.1
              },
              {
                "word": "our",
                "start": 1.1,
                "end": 1.3
              }
            ],
            "segments": [
              {
                "id": 0,
                "seek": 0,
                "start": 0.5,
                "end": 8.0,
                "text": "Welcome to our team meeting.",
                "tokens": [50364, 14618, 281, 527, 1469, 3440, 13, 50564],
                "temperature": 0.0,
                "avg_logprob": -0.15,
                "no_speech_prob": 0.01
              }
            ]
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Translation (Speech-to-Text in English)</h2>
        <CodeBlock
          title="Audio Translation"
          language="bash"
          id="speech-translation"
          code={`curl -X POST https://p.qmtju.com/v1/speech/translations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "file=@french_audio.mp3" \\
  -F "model=whisper-large" \\
  -F "response_format=json"`}
        />

        <JsonResponse
          title="Translation Response"
          id="speech-translation-response"
          data={{
            "text": "Hello, how are you today? I hope you are doing well."
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Supported Audio Formats</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <Mic className="h-6 w-6 text-gray-600 mx-auto mb-1" />
            <p className="text-sm font-medium">MP3</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <Mic className="h-6 w-6 text-gray-600 mx-auto mb-1" />
            <p className="text-sm font-medium">WAV</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <Mic className="h-6 w-6 text-gray-600 mx-auto mb-1" />
            <p className="text-sm font-medium">FLAC</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <Mic className="h-6 w-6 text-gray-600 mx-auto mb-1" />
            <p className="text-sm font-medium">M4A</p>
          </div>
        </div>
        <p className="text-sm text-gray-600">Maximum file size: 25MB</p>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Parameters</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">file</td>
                <td className="px-4 py-3 text-sm text-gray-600">file</td>
                <td className="px-4 py-3 text-sm text-gray-600">Audio file to transcribe</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Speech recognition model to use</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">language</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Language code (e.g., "en", "es", "fr")</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">response_format</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">"json", "text", "srt", "verbose_json", "vtt"</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">temperature</td>
                <td className="px-4 py-3 text-sm text-gray-600">number</td>
                <td className="px-4 py-3 text-sm text-gray-600">Sampling temperature (0-1)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-green-900 mb-3">🎙️ Speech Recognition Tips</h3>
        <ul className="space-y-2 text-green-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use high-quality audio recordings for better accuracy
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Specify the language when known for improved results
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use timestamps for creating subtitles or captions
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Break long audio files into smaller chunks for processing
          </li>
        </ul>
      </div>
    </div>
  );

  case 'tts':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Text-to-Speech</h1>
      <p className="text-xl text-gray-600 mb-8">
        Convert text to natural-sounding speech using advanced TTS models. This is an asynchronous operation.
      </p>

      <ApiEndpoint
        method="POST"
        endpoint="/v1/tts/speech"
        description="Generate speech from text using text-to-speech models (async operation)"
      />


      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Submit TTS Task</h2>
        <CodeBlock
          title="Create TTS Task"
          language="bash"
          id="tts-submit"
          code={`curl -X POST https://p.qmtju.com/v1/tts/speech \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "F5-TTS",
    "input": "Hello! Welcome to our text-to-speech service. This is a demonstration of our advanced AI voice synthesis technology.",
    "voice": "alloy",
    "response_format": "mp3",
    "speed": 1.0
  }'`}
        />

        <JsonResponse
          title="Task Submitted"
          id="tts-submit-response"
          data={{
            "id": "tts-task-abc123",
            "object": "task_status",
            "status": "pending",
            "task_type": "tts_synthesis",
            "model": "F5-TTS",
            "created_at": "2025-08-13T10:30:00Z",
            "estimated_completion": "2025-08-13T10:30:30Z"
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Check Task Status</h2>
        <CodeBlock
          title="Get Task Status"
          language="bash"
          id="tts-status"
          code={`curl https://p.qmtju.com/v1/tasks/tts-task-abc123 \\
  -H "x-api-key: mp_live_your_api_key_here"`}
        />

        <JsonResponse
          title="Completed Task"
          id="tts-status-response"
          data={{
            "id": "tts-task-abc123",
            "status": "completed",
            "task_type": "tts_synthesis",
            "model": "F5-TTS",
            "created_at": "2025-08-13T10:30:00Z",
            "completed_at": "2025-08-13T10:30:25Z",
            "cost": 0.05,
            "result": {
              "file_url": "https://platform-storage.com/audio/tts-output-abc123.mp3",
              "duration": 8.2,
              "format": "mp3"
            }
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Voice Cloning</h2>
        <p className="text-gray-600 mb-4">
          Some models support voice cloning using reference audio:
        </p>
        
        <CodeBlock
          title="Voice Cloning Request"
          language="bash"
          id="tts-voice-clone"
          code={`curl -X POST https://p.qmtju.com/v1/tts/speech \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "model=MOSS-TTSD-v0.5" \\
  -F "input=This is a test of voice cloning technology using the provided reference audio." \\
  -F "reference_audio=@reference_voice.wav" \\
  -F "response_format=wav"`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Voices</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium">alloy</p>
            <p className="text-xs text-gray-600">Neutral</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium">echo</p>
            <p className="text-xs text-gray-600">Male</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium">fable</p>
            <p className="text-xs text-gray-600">British</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium">onyx</p>
            <p className="text-xs text-gray-600">Deep</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium">nova</p>
            <p className="text-xs text-gray-600">Female</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium">shimmer</p>
            <p className="text-xs text-gray-600">Soft</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-sm font-medium">custom</p>
            <p className="text-xs text-gray-600">Upload Reference</p>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Parameters</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">TTS model to use</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">input</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Text to convert to speech</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">voice</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Voice identifier or "custom"</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">response_format</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">"mp3", "wav", "flac", "aac"</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">speed</td>
                <td className="px-4 py-3 text-sm text-gray-600">number</td>
                <td className="px-4 py-3 text-sm text-gray-600">Speech speed (0.25 - 4.0)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">📊 TTS Best Practices</h3>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use shorter text segments for faster processing
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Include punctuation for natural speech rhythm
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Test different voices to find the best match
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use SSML tags for advanced speech control (if supported)
          </li>
        </ul>
      </div>
    </div>
  );

  case 'video-generation':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Video Generation</h1>
      <p className="text-xl text-gray-600 mb-8">
        Create videos from text descriptions or transform images into video clips. This is an asynchronous operation.
      </p>

      <ApiEndpoint
        method="POST"
        endpoint="/v1/videos/generations"
        description="Generate videos from text or images (async operation)"
      />


      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Image-to-Video Generation</h2>
        <CodeBlock
          title="Image to Video"
          language="bash"
          id="video-i2v"
          code={`curl -X POST https://p.qmtju.com/v1/videos/generations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "model=Wan2.2-I2V-A14B" \\
  -F "image=@input_image.jpg" \\
  -F "prompt=A gentle breeze makes the leaves rustle softly" \\
  -F "duration=5" \\
  -F "fps=24"`}
        />

        <JsonResponse
          title="Video Task Submitted"
          id="video-i2v-response"
          data={{
            "id": "video-task-abc123",
            "object": "task_status", 
            "status": "pending",
            "task_type": "video_generation",
            "model": "Wan2.2-I2V-A14B",
            "created_at": "2025-08-13T10:30:00Z",
            "estimated_completion": "2025-08-13T10:35:00Z"
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Text-to-Video Generation</h2>
        <CodeBlock
          title="Text to Video"
          language="bash"
          id="video-t2v"
          code={`curl -X POST https://p.qmtju.com/v1/videos/generations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "stepvideo-t2v",
    "prompt": "A majestic eagle soaring through snow-capped mountains with clouds drifting by, cinematic quality, 4K resolution",
    "duration": 8,
    "resolution": "1280x720",
    "fps": 30,
    "style": "cinematic"
  }'`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Check Video Generation Status</h2>
        <CodeBlock
          title="Get Video Task Status"
          language="bash"
          id="video-status"
          code={`curl https://p.qmtju.com/v1/tasks/video-task-abc123 \\
  -H "x-api-key: mp_live_your_api_key_here"`}
        />

        <JsonResponse
          title="Completed Video Task"
          id="video-status-response"
          data={{
            "id": "video-task-abc123",
            "status": "completed",
            "task_type": "video_generation", 
            "model": "Wan2.2-I2V-A14B",
            "created_at": "2025-08-13T10:30:00Z",
            "completed_at": "2025-08-13T10:34:30Z",
            "cost": 0.50,
            "result": {
              "file_url": "https://platform-storage.com/videos/output-abc123.mp4",
              "duration": 5.0,
              "resolution": "1280x720",
              "fps": 24,
              "format": "mp4"
            }
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Supported Formats & Specifications</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">📺 Output Formats</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• MP4 (H.264)</li>
              <li>• WebM</li>
              <li>• MOV</li>
              <li>• GIF (for short clips)</li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">⚙️ Specifications</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Duration: 1-30 seconds</li>
              <li>• FPS: 15, 24, 30</li>
              <li>• Resolution: 720p, 1080p</li>
              <li>• Input images: JPG, PNG</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Parameters</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Video generation model to use</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">prompt</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Text description of the video</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">image</td>
                <td className="px-4 py-3 text-sm text-gray-600">file</td>
                <td className="px-4 py-3 text-sm text-gray-600">Input image for I2V models (optional)</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">duration</td>
                <td className="px-4 py-3 text-sm text-gray-600">integer</td>
                <td className="px-4 py-3 text-sm text-gray-600">Video duration in seconds (1-30)</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">fps</td>
                <td className="px-4 py-3 text-sm text-gray-600">integer</td>
                <td className="px-4 py-3 text-sm text-gray-600">Frames per second (15, 24, 30)</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">resolution</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Output resolution (e.g., "1280x720")</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-3">🎬 Video Generation Tips</h3>
        <ul className="space-y-2 text-red-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use detailed, cinematic prompts for better video quality
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Shorter durations (2-8 seconds) typically produce better results
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            For I2V, use high-quality input images with clear subjects
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Video generation can take 5-15 minutes depending on duration
          </li>
        </ul>
      </div>
    </div>
  );

  case '3d-generation':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">3D Generation</h1>
      <p className="text-xl text-gray-600 mb-8">
        Convert 2D images into 3D models using advanced AI. This is an asynchronous operation that produces GLB files.
      </p>

      <ApiEndpoint
        method="POST"
        endpoint="/v1/3d/generations"
        description="Generate 3D models from 2D images (async operation)"
      />


      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Generate 3D Model</h2>
        <CodeBlock
          title="Image to 3D Model"
          language="bash"
          id="3d-generation"
          code={`curl -X POST https://p.qmtju.com/v1/3d/generations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "model=Hi3DGen" \\
  -F "image=@product_image.jpg" \\
  -F "file_format=glb" \\
  -F "texture_resolution=1024" \\
  -F "mesh_quality=high"`}
        />

        <JsonResponse
          title="3D Task Submitted"
          id="3d-submit-response"
          data={{
            "id": "3d-task-abc123",
            "object": "task_status",
            "status": "pending",
            "task_type": "3d_generation",
            "model": "Hi3DGen",
            "created_at": "2025-08-13T10:30:00Z",
            "estimated_completion": "2025-08-13T10:40:00Z"
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Check 3D Generation Status</h2>
        <CodeBlock
          title="Get 3D Task Status"
          language="bash"
          id="3d-status"
          code={`curl https://p.qmtju.com/v1/tasks/3d-task-abc123 \\
  -H "x-api-key: mp_live_your_api_key_here"`}
        />

        <JsonResponse
          title="Completed 3D Task"
          id="3d-status-response"
          data={{
            "id": "3d-task-abc123",
            "status": "completed",
            "task_type": "3d_generation",
            "model": "Hi3DGen",
            "created_at": "2025-08-13T10:30:00Z",
            "completed_at": "2025-08-13T10:38:45Z",
            "cost": 0.20,
            "result": {
              "file_url": "https://platform-storage.com/3d/model-abc123.glb",
              "format": "glb",
              "file_size": 2048576,
              "vertices": 15420,
              "faces": 8734,
              "texture_resolution": "1024x1024"
            }
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Input Requirements</h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-3">📋 Image Requirements</h3>
          <ul className="space-y-2 text-yellow-800">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <strong>Format:</strong> JPG, PNG, WebP
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <strong>Resolution:</strong> Minimum 512x512, recommended 1024x1024
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <strong>Subject:</strong> Clear, well-lit object with good contrast
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <strong>Background:</strong> Simple or transparent background works best
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <strong>File Size:</strong> Maximum 10MB
            </li>
          </ul>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Output Formats</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <Layers className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900 mb-1">GLB</h3>
            <p className="text-sm text-gray-600">Binary glTF format, recommended for web</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <Layers className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900 mb-1">OBJ</h3>
            <p className="text-sm text-gray-600">Wavefront OBJ format for 3D software</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <Layers className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900 mb-1">FBX</h3>
            <p className="text-sm text-gray-600">Autodesk format for game engines</p>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Parameters</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">3D generation model to use</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">image</td>
                <td className="px-4 py-3 text-sm text-gray-600">file</td>
                <td className="px-4 py-3 text-sm text-gray-600">Input image file</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">file_format</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">"glb", "obj", "fbx" (default: "glb")</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">texture_resolution</td>
                <td className="px-4 py-3 text-sm text-gray-600">integer</td>
                <td className="px-4 py-3 text-sm text-gray-600">Texture size: 512, 1024, 2048</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">mesh_quality</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">"low", "medium", "high"</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Use Cases</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">🛍️ E-commerce</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Product visualization</li>
              <li>• AR/VR shopping experiences</li>
              <li>• 360° product views</li>
              <li>• Interactive catalogs</li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">🎮 Gaming & Media</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Game asset creation</li>
              <li>• Character modeling</li>
              <li>• Environment props</li>
              <li>• Animation ready models</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">🏗️ Architecture</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Building visualization</li>
              <li>• Interior design</li>
              <li>• Virtual walkthroughs</li>
              <li>• Concept modeling</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">📚 Education</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Interactive learning materials</li>
              <li>• Scientific visualization</li>
              <li>• Historical reconstruction</li>
              <li>• Virtual museums</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">🎯 3D Generation Tips</h3>
        <ul className="space-y-2 text-purple-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use images with clear, well-defined objects for better results
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Avoid cluttered backgrounds or multiple objects
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Higher texture resolution increases file size but improves quality
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            GLB format is recommended for web-based 3D viewers
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Generation typically takes 5-10 minutes depending on complexity
          </li>
        </ul>
      </div>
    </div>
  );

  case 'embeddings':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Embeddings</h1>
      <p className="text-xl text-gray-600 mb-8">
        Convert text into high-dimensional vector representations for semantic search, similarity analysis, and machine learning applications.
      </p>

      <ApiEndpoint
        method="POST"
        endpoint="/v1/embeddings"
        description="Generate vector embeddings for text inputs"
      />


      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Generate Embeddings</h2>
        <CodeBlock
          title="Single Text Embedding"
          language="bash"
          id="embedding-single"
          code={`curl -X POST https://p.qmtju.com/v1/embeddings \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "text-embedding-3-large",
    "input": "The quick brown fox jumps over the lazy dog",
    "encoding_format": "float"
  }'`}
        />

        <JsonResponse
          title="Embedding Response"
          id="embedding-response"
          data={{
            "object": "list",
            "data": [
              {
                "object": "embedding",
                "index": 0,
                "embedding": [
                  -0.006929283495992422,
                  -0.005336422007530928,
                  0.014793063513934612,
                  "... (3072 dimensions total)"
                ]
              }
            ],
            "model": "text-embedding-3-large",
            "usage": {
              "prompt_tokens": 10,
              "total_tokens": 10
            }
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Batch Embeddings</h2>
        <CodeBlock
          title="Multiple Text Embeddings"
          language="bash"
          id="embedding-batch"
          code={`curl -X POST https://p.qmtju.com/v1/embeddings \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "bge-m3",
    "input": [
      "Machine learning is a subset of artificial intelligence",
      "Natural language processing helps computers understand text",
      "Computer vision enables machines to interpret visual information"
    ],
    "encoding_format": "float"
  }'`}
        />

        <JsonResponse
          title="Batch Embedding Response"
          id="embedding-batch-response"
          data={{
            "object": "list",
            "data": [
              {
                "object": "embedding",
                "index": 0,
                "embedding": [-0.008, 0.012, 0.005, "..."]
              },
              {
                "object": "embedding", 
                "index": 1,
                "embedding": [0.003, -0.009, 0.015, "..."]
              },
              {
                "object": "embedding",
                "index": 2,
                "embedding": [0.007, 0.002, -0.011, "..."]
              }
            ],
            "model": "bge-m3",
            "usage": {
              "prompt_tokens": 32,
              "total_tokens": 32
            }
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Similarity Search Example</h2>
        <CodeBlock
          title="Python Similarity Search"
          language="python"
          id="similarity-search"
          code={`import numpy as np
from sklearn.metrics.pairwise import cosine_similarity

# Sample embeddings from API
query_embedding = np.array([...])  # Your query embedding
doc_embeddings = np.array([...])   # Document embeddings

# Calculate cosine similarity
similarities = cosine_similarity([query_embedding], doc_embeddings)[0]

# Get top 5 most similar documents
top_indices = np.argsort(similarities)[::-1][:5]
top_similarities = similarities[top_indices]

for i, (idx, score) in enumerate(zip(top_indices, top_similarities)):
    print(f"Rank {i+1}: Document {idx} (Score: {score:.4f})")`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Supported Parameters</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Embedding model to use</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">input</td>
                <td className="px-4 py-3 text-sm text-gray-600">string | array</td>
                <td className="px-4 py-3 text-sm text-gray-600">Text or array of texts to embed</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">encoding_format</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">"float" or "base64" (default: "float")</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">dimensions</td>
                <td className="px-4 py-3 text-sm text-gray-600">integer</td>
                <td className="px-4 py-3 text-sm text-gray-600">Reduce embedding dimensions (optional)</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">user</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">User identifier for tracking (optional)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Model Specifications</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Model</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Dimensions</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Context Length</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Languages</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">text-embedding-3-large</td>
                <td className="px-4 py-3 text-sm text-gray-600">3072</td>
                <td className="px-4 py-3 text-sm text-gray-600">8191 tokens</td>
                <td className="px-4 py-3 text-sm text-gray-600">100+ languages</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">bge-m3</td>
                <td className="px-4 py-3 text-sm text-gray-600">1024</td>
                <td className="px-4 py-3 text-sm text-gray-600">8192 tokens</td>
                <td className="px-4 py-3 text-sm text-gray-600">100+ languages</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">jina-embeddings-v3</td>
                <td className="px-4 py-3 text-sm text-gray-600">1024</td>
                <td className="px-4 py-3 text-sm text-gray-600">8192 tokens</td>
                <td className="px-4 py-3 text-sm text-gray-600">English + 30 languages</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Use Cases</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">🔍 Semantic Search</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Document retrieval systems</li>
              <li>• Knowledge base search</li>
              <li>• FAQ matching</li>
              <li>• Content recommendation</li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">📊 Text Analysis</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Similarity measurement</li>
              <li>• Text clustering</li>
              <li>• Duplicate detection</li>
              <li>• Content classification</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">🤖 RAG Applications</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Retrieval-augmented generation</li>
              <li>• Context-aware chatbots</li>
              <li>• Question answering systems</li>
              <li>• Knowledge retrieval</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">🎯 ML Features</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Feature engineering</li>
              <li>• Recommendation systems</li>
              <li>• Anomaly detection</li>
              <li>• Transfer learning</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-cyan-900 mb-3">🧠 Embedding Best Practices</h3>
        <ul className="space-y-2 text-cyan-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-cyan-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use consistent text preprocessing across queries and documents
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-cyan-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Choose models with appropriate dimensionality for your use case
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-cyan-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Cache embeddings for frequently used texts to reduce costs
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-cyan-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use batch processing for better efficiency with multiple texts
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-cyan-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Consider text length limits when chunking large documents
          </li>
        </ul>
      </div>
    </div>
  );

  case 'moderation':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Content Moderation</h1>
      <p className="text-xl text-gray-600 mb-8">
        Analyze and filter content for safety, detecting harmful, offensive, or inappropriate material across text and images.
      </p>

      <ApiEndpoint
        method="POST"
        endpoint="/v1/moderations"
        description="Analyze content for policy violations and safety issues"
      />

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Text Moderation</h2>
        <CodeBlock
          title="Moderate Text Content"
          language="bash"
          id="moderation-text"
          code={`curl -X POST https://p.qmtju.com/v1/moderations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "text-moderation-latest",
    "input": "This is a sample text that needs to be checked for inappropriate content.",
    "categories": ["hate", "harassment", "violence", "sexual", "self-harm"]
  }'`}
        />

        <JsonResponse
          title="Text Moderation Response"
          id="moderation-text-response"
          data={{
            "id": "modr-abc123",
            "model": "text-moderation-latest",
            "results": [
              {
                "flagged": false,
                "categories": {
                  "hate": false,
                  "hate/threatening": false,
                  "harassment": false,
                  "harassment/threatening": false,
                  "sexual": false,
                  "sexual/minors": false,
                  "violence": false,
                  "violence/graphic": false,
                  "self-harm": false,
                  "self-harm/intent": false,
                  "self-harm/instructions": false
                },
                "category_scores": {
                  "hate": 0.0001,
                  "hate/threatening": 0.0000,
                  "harassment": 0.0002,
                  "harassment/threatening": 0.0000,
                  "sexual": 0.0001,
                  "sexual/minors": 0.0000,
                  "violence": 0.0001,
                  "violence/graphic": 0.0000,
                  "self-harm": 0.0000,
                  "self-harm/intent": 0.0000,
                  "self-harm/instructions": 0.0000
                }
              }
            ]
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Image Moderation</h2>
        <CodeBlock
          title="Moderate Image Content"
          language="bash"
          id="moderation-image"
          code={`curl -X POST https://p.qmtju.com/v1/moderations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -F "model=image-moderation-005" \\
  -F "image=@content_image.jpg" \\
  -F "categories[]=adult" \\
  -F "categories[]=violence" \\
  -F "categories[]=racy"`}
        />

        <JsonResponse
          title="Image Moderation Response"
          id="moderation-image-response"
          data={{
            "id": "modr-img-abc123",
            "model": "image-moderation-005",
            "results": [
              {
                "flagged": false,
                "categories": {
                  "adult": false,
                  "violence": false,
                  "racy": false,
                  "medical": false,
                  "spoof": false
                },
                "category_scores": {
                  "adult": 0.12,
                  "violence": 0.05,
                  "racy": 0.08,
                  "medical": 0.02,
                  "spoof": 0.01
                },
                "detected_objects": [
                  {
                    "label": "person",
                    "confidence": 0.95
                  }
                ]
              }
            ]
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Batch Moderation</h2>
        <CodeBlock
          title="Moderate Multiple Texts"
          language="bash"
          id="moderation-batch"
          code={`curl -X POST https://p.qmtju.com/v1/moderations \\
  -H "x-api-key: mp_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "text-moderation-latest",
    "input": [
      "First text to moderate",
      "Second text to check",
      "Third piece of content"
    ]
  }'`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Moderation Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">📝 Text Categories</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                <strong>Hate:</strong> Content promoting hatred
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                <strong>Harassment:</strong> Threatening or bullying content
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                <strong>Violence:</strong> Violent or graphic content
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                <strong>Sexual:</strong> Sexual or adult content
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                <strong>Self-harm:</strong> Content promoting self-injury
              </li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">🖼️ Image Categories</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                <strong>Adult:</strong> Explicit adult content
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                <strong>Violence:</strong> Violent or disturbing imagery
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                <strong>Racy:</strong> Suggestive content
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                <strong>Medical:</strong> Medical or anatomical content
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                <strong>Spoof:</strong> Fake or manipulated content
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Parameters</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">model</td>
                <td className="px-4 py-3 text-sm text-gray-600">string</td>
                <td className="px-4 py-3 text-sm text-gray-600">Moderation model to use</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">input</td>
                <td className="px-4 py-3 text-sm text-gray-600">string | array</td>
                <td className="px-4 py-3 text-sm text-gray-600">Text content to moderate</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">image</td>
                <td className="px-4 py-3 text-sm text-gray-600">file</td>
                <td className="px-4 py-3 text-sm text-gray-600">Image file to moderate</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">categories</td>
                <td className="px-4 py-3 text-sm text-gray-600">array</td>
                <td className="px-4 py-3 text-sm text-gray-600">Specific categories to check</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">threshold</td>
                <td className="px-4 py-3 text-sm text-gray-600">number</td>
                <td className="px-4 py-3 text-sm text-gray-600">Custom flagging threshold (0.0-1.0)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Integration Example</h2>
        <CodeBlock
          title="Content Filtering Pipeline"
          language="python"
          id="moderation-pipeline"
          code={`import requests

def moderate_content(text, threshold=0.7):
    """
    Moderate text content and return safety status
    """
    response = requests.post(
        'https://p.qmtju.com/v1/moderations',
        headers={
            'x-api-key': 'mp_live_your_api_key_here',
            'Content-Type': 'application/json'
        },
        json={
            'model': 'text-moderation-latest',
            'input': text
        }
    )
    
    result = response.json()['results'][0]
    
    # Check if content is flagged
    if result['flagged']:
        return {
            'safe': False,
            'reason': 'Content flagged by moderation system',
            'categories': [cat for cat, flagged in result['categories'].items() if flagged]
        }
    
    # Check custom threshold
    high_scores = []
    for category, score in result['category_scores'].items():
        if score > threshold:
            high_scores.append(category)
    
    if high_scores:
        return {
            'safe': False,
            'reason': f'Content exceeds threshold for: {", ".join(high_scores)}',
            'scores': {cat: result['category_scores'][cat] for cat in high_scores}
        }
    
    return {'safe': True, 'reason': 'Content passed moderation checks'}

# Usage example
user_input = "This is some user-generated content"
moderation_result = moderate_content(user_input)

if moderation_result['safe']:
    print("Content is safe to publish")
else:
    print(f"Content blocked: {moderation_result['reason']}")`}
        />
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-3">🛡️ Content Moderation Best Practices</h3>
        <ul className="space-y-2 text-red-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Implement moderation before publishing user-generated content
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use multiple moderation layers for comprehensive protection
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Set appropriate thresholds based on your platform's requirements
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Provide appeals process for false positives
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Log moderation results for compliance and improvement
          </li>
        </ul>
      </div>
    </div>
  );

  case 'async-tasks':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Async Tasks</h1>
      <p className="text-xl text-gray-600 mb-8">
        Learn how to handle long-running operations like video generation, 3D modeling, and large file processing using our async task system.
      </p>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">How Async Tasks Work</h2>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <h3 className="font-semibold text-blue-900 mb-2">Submit Task</h3>
              <p className="text-sm text-blue-700">Send request to async endpoint</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">2</span>
              </div>
              <h3 className="font-semibold text-blue-900 mb-2">Get Task ID</h3>
              <p className="text-sm text-blue-700">Receive unique task identifier</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">3</span>
              </div>
              <h3 className="font-semibold text-blue-900 mb-2">Poll Status</h3>
              <p className="text-sm text-blue-700">Check task progress periodically</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">4</span>
              </div>
              <h3 className="font-semibold text-blue-900 mb-2">Get Result</h3>
              <p className="text-sm text-blue-700">Download completed output</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Task Status Endpoints</h2>
        
        <ApiEndpoint
          method="GET"
          endpoint="/v1/tasks/{task_id}"
          description="Get the status and result of a specific task"
        />

        <ApiEndpoint
          method="GET"
          endpoint="/v1/tasks"
          description="List all tasks with optional filtering"
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Check Task Status</h2>
        <CodeBlock
          title="Get Task Status"
          language="bash"
          id="task-status"
          code={`curl https://p.qmtju.com/v1/tasks/task-abc123 \\
  -H "x-api-key: mp_live_your_api_key_here"`}
        />

        <JsonResponse
          title="Task Status Response"
          id="task-status-response"
          data={{
            "id": "task-abc123",
            "status": "processing",
            "task_type": "video_generation",
            "model": "stepvideo-t2v",
            "created_at": "2025-08-13T10:30:00Z",
            "updated_at": "2025-08-13T10:32:15Z",
            "estimated_completion": "2025-08-13T10:35:00Z",
            "progress": 65,
            "cost": null,
            "result": null
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Task Status Values</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Action Required</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    pending
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">Task is queued and waiting to start</td>
                <td className="px-4 py-3 text-sm text-gray-600">Wait and poll periodically</td>
              </tr>
              <tr>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    processing
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">Task is currently being executed</td>
                <td className="px-4 py-3 text-sm text-gray-600">Continue polling for updates</td>
              </tr>
              <tr>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    completed
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">Task finished successfully</td>
                <td className="px-4 py-3 text-sm text-gray-600">Download result from URL</td>
              </tr>
              <tr>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    failed
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">Task encountered an error</td>
                <td className="px-4 py-3 text-sm text-gray-600">Check error details and retry</td>
              </tr>
              <tr>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    cancelled
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">Task was cancelled by user or system</td>
                <td className="px-4 py-3 text-sm text-gray-600">Resubmit if needed</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Polling Implementation</h2>
        <CodeBlock
          title="JavaScript Polling Example"
          language="javascript"
          id="polling-example"
          code={`async function pollTaskStatus(taskId, maxAttempts = 60) {
  const pollInterval = 5000; // 5 seconds
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetch(\`https://p.qmtju.com/v1/tasks/\${taskId}\`, {
        headers: {
          'x-api-key': 'mp_live_your_api_key_here'
        }
      });
      
      const task = await response.json();
      
      console.log(\`Task \${taskId} status: \${task.status}\`);
      
      if (task.status === 'completed') {
        console.log('Task completed successfully!');
        console.log('Result URL:', task.result.file_url);
        return task;
      }
      
      if (task.status === 'failed') {
        throw new Error(\`Task failed: \${task.error?.message || 'Unknown error'}\`);
      }
      
      if (task.status === 'cancelled') {
        throw new Error('Task was cancelled');
      }
      
      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
    } catch (error) {
      console.error('Error polling task status:', error);
      throw error;
    }
  }
  
  throw new Error('Task polling timeout - maximum attempts reached');
}

// Usage
const taskId = 'task-abc123';
pollTaskStatus(taskId)
  .then(completedTask => {
    console.log('Final result:', completedTask.result);
  })
  .catch(error => {
    console.error('Task failed:', error.message);
  });`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">List All Tasks</h2>
        <CodeBlock
          title="Get Task List"
          language="bash"
          id="task-list"
          code={`curl "https://p.qmtju.com/v1/tasks?status=completed&limit=10" \\
  -H "x-api-key: mp_live_your_api_key_here"`}
        />

        <JsonResponse
          title="Task List Response"
          id="task-list-response"
          data={{
            "object": "list",
            "data": [
              {
                "id": "task-abc123",
                "status": "completed",
                "task_type": "video_generation",
                "created_at": "2025-08-13T10:30:00Z",
                "completed_at": "2025-08-13T10:34:30Z"
              },
              {
                "id": "task-def456",
                "status": "processing",
                "task_type": "3d_generation",
                "created_at": "2025-08-13T10:32:00Z",
                "progress": 45
              }
            ],
            "has_more": false
          }}
        />
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-green-900 mb-3">⚡ Async Task Best Practices</h3>
        <ul className="space-y-2 text-green-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Poll at reasonable intervals (5-10 seconds) to avoid rate limits
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Implement exponential backoff for failed requests
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Set reasonable timeout limits based on task type
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Store task IDs persistently for long-running operations
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Handle network errors gracefully with retry logic
          </li>
        </ul>
      </div>
    </div>
  );

  case 'rate-limits':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Rate Limits</h1>
      <p className="text-xl text-gray-600 mb-8">
        Understand API rate limits, how to handle them gracefully, and optimize your application's request patterns.
      </p>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Rate Limit Structure</h2>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <p className="text-blue-800 mb-4">
            Rate limits are applied per API key and are measured in different units depending on the endpoint:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Requests per Minute</h3>
              <p className="text-sm text-blue-700">Standard API calls</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Tokens per Minute</h3>
              <p className="text-sm text-blue-700">Text generation models</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Images per Hour</h3>
              <p className="text-sm text-blue-700">Image generation models</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Rate Limit Headers</h2>
        <p className="text-gray-600 mb-4">
          Every API response includes rate limit information in the headers:
        </p>
        
        <CodeBlock
          title="Rate Limit Headers"
          language="http"
          id="rate-limit-headers"
          code={`HTTP/1.1 200 OK
x-ratelimit-limit-requests: 100
x-ratelimit-remaining-requests: 95
x-ratelimit-reset-requests: 1692901800
x-ratelimit-limit-tokens: 50000
x-ratelimit-remaining-tokens: 48500
x-ratelimit-reset-tokens: 1692901800`}
        />

        <div className="mt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Header Descriptions</h3>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Header</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">x-ratelimit-limit-requests</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Maximum requests allowed per window</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">x-ratelimit-remaining-requests</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Requests remaining in current window</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">x-ratelimit-reset-requests</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Unix timestamp when limit resets</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">x-ratelimit-limit-tokens</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Maximum tokens allowed per window</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">x-ratelimit-remaining-tokens</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Tokens remaining in current window</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">x-ratelimit-reset-tokens</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Unix timestamp when token limit resets</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Rate Limit by Endpoint</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Endpoint Category</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Requests/Min</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Tokens/Min</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Special Limits</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Text Generation</td>
                <td className="px-4 py-3 text-sm text-gray-600">100</td>
                <td className="px-4 py-3 text-sm text-gray-600">50,000</td>
                <td className="px-4 py-3 text-sm text-gray-600">-</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Image Generation</td>
                <td className="px-4 py-3 text-sm text-gray-600">20</td>
                <td className="px-4 py-3 text-sm text-gray-600">-</td>
                <td className="px-4 py-3 text-sm text-gray-600">100 images/hour</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Speech Recognition</td>
                <td className="px-4 py-3 text-sm text-gray-600">50</td>
                <td className="px-4 py-3 text-sm text-gray-600">-</td>
                <td className="px-4 py-3 text-sm text-gray-600">60 minutes/hour</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Embeddings</td>
                <td className="px-4 py-3 text-sm text-gray-600">200</td>
                <td className="px-4 py-3 text-sm text-gray-600">100,000</td>
                <td className="px-4 py-3 text-sm text-gray-600">-</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Video Generation</td>
                <td className="px-4 py-3 text-sm text-gray-600">5</td>
                <td className="px-4 py-3 text-sm text-gray-600">-</td>
                <td className="px-4 py-3 text-sm text-gray-600">10 videos/hour</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Moderation</td>
                <td className="px-4 py-3 text-sm text-gray-600">500</td>
                <td className="px-4 py-3 text-sm text-gray-600">200,000</td>
                <td className="px-4 py-3 text-sm text-gray-600">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Rate Limit Exceeded Response</h2>
        <p className="text-gray-600 mb-4">
          When you exceed rate limits, you'll receive a 429 status code:
        </p>
        
        <JsonResponse
          title="429 Rate Limit Exceeded"
          id="rate-limit-error"
          data={{
            "error": {
              "type": "rate_limit_exceeded",
              "message": "Rate limit exceeded. Please try again in 45 seconds.",
              "retry_after": 45
            }
          }}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Handling Rate Limits</h2>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Rate Limit Handler</h3>
        <CodeBlock
          title="JavaScript Rate Limit Handler"
          language="javascript"
          id="rate-limit-handler"
          code={`class RateLimitHandler {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseUrl = 'https://p.qmtju.com';
  }

  async makeRequest(endpoint, options = {}) {
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount <= maxRetries) {
      try {
        const response = await fetch(\`\${this.baseUrl}\${endpoint}\`, {
          ...options,
          headers: {
            'x-api-key': this.apiKey,
            'Content-Type': 'application/json',
            ...options.headers
          }
        });

        // Check rate limit headers
        const remaining = parseInt(response.headers.get('x-ratelimit-remaining-requests') || '0');
        const resetTime = parseInt(response.headers.get('x-ratelimit-reset-requests') || '0');

        if (response.status === 429) {
          const retryAfter = parseInt(response.headers.get('retry-after') || '60');
          
          if (retryCount < maxRetries) {
            console.log(\`Rate limited. Waiting \${retryAfter} seconds before retry \${retryCount + 1}/\${maxRetries}\`);
            await this.delay(retryAfter * 1000);
            retryCount++;
            continue;
          } else {
            throw new Error('Rate limit exceeded. Max retries reached.');
          }
        }

        if (!response.ok) {
          throw new Error(\`HTTP \${response.status}: \${response.statusText}\`);
        }

        // Log remaining requests for monitoring
        console.log(\`Remaining requests: \${remaining}\`);
        
        return await response.json();

      } catch (error) {
        if (retryCount === maxRetries) {
          throw error;
        }
        retryCount++;
        await this.delay(1000 * retryCount); // Exponential backoff
      }
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Usage
const client = new RateLimitHandler('mp_live_your_api_key_here');

client.makeRequest('/v1/text/chat/completions', {
  method: 'POST',
  body: JSON.stringify({
    model: 'DeepSeek-V3',
    messages: [{ role: 'user', content: 'Hello!' }]
  })
}).then(result => {
  console.log('Success:', result);
}).catch(error => {
  console.error('Error:', error.message);
});`}
        />

        <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">Advanced Request Queue</h3>
        <CodeBlock
          title="Request Queue with Rate Limiting"
          language="javascript"
          id="request-queue"
          code={`class RequestQueue {
  constructor(apiKey, requestsPerMinute = 100) {
    this.apiKey = apiKey;
    this.requestsPerMinute = requestsPerMinute;
    this.queue = [];
    this.processing = false;
    this.requestTimes = [];
  }

  async add(endpoint, options) {
    return new Promise((resolve, reject) => {
      this.queue.push({ endpoint, options, resolve, reject });
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.processing || this.queue.length === 0) return;
    
    this.processing = true;

    while (this.queue.length > 0) {
      // Clean old request times (older than 1 minute)
      const oneMinuteAgo = Date.now() - 60000;
      this.requestTimes = this.requestTimes.filter(time => time > oneMinuteAgo);

      // Check if we can make a request
      if (this.requestTimes.length >= this.requestsPerMinute) {
        const oldestRequest = Math.min(...this.requestTimes);
        const waitTime = oldestRequest + 60000 - Date.now();
        if (waitTime > 0) {
          console.log(\`Rate limit reached. Waiting \${waitTime}ms\`);
          await this.delay(waitTime);
          continue;
        }
      }

      const { endpoint, options, resolve, reject } = this.queue.shift();
      
      try {
        this.requestTimes.push(Date.now());
        const response = await fetch(\`https://p.qmtju.com\${endpoint}\`, {
          ...options,
          headers: {
            'x-api-key': this.apiKey,
            'Content-Type': 'application/json',
            ...options.headers
          }
        });

        if (!response.ok) {
          throw new Error(\`HTTP \${response.status}: \${response.statusText}\`);
        }

        resolve(await response.json());
      } catch (error) {
        reject(error);
      }

      // Small delay between requests
      await this.delay(100);
    }

    this.processing = false;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Usage
const queue = new RequestQueue('mp_live_your_api_key_here', 50); // 50 requests per minute

// Add multiple requests
for (let i = 0; i < 10; i++) {
  queue.add('/v1/text/chat/completions', {
    method: 'POST',
    body: JSON.stringify({
      model: 'DeepSeek-V3',
      messages: [{ role: 'user', content: \`Request \${i + 1}\` }]
    })
  }).then(result => {
    console.log(\`Request \${i + 1} completed\`);
  }).catch(error => {
    console.error(\`Request \${i + 1} failed:\`, error.message);
  });
}`}
        />
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-orange-900 mb-3">⚠️ Rate Limit Best Practices</h3>
        <ul className="space-y-2 text-orange-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Monitor rate limit headers in every response
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Implement exponential backoff for retry logic
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Use request queuing to avoid bursts
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Cache responses when possible to reduce API calls
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Batch requests when supported by the endpoint
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-orange-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Consider upgrading your plan for higher limits
          </li>
        </ul>
      </div>
    </div>
  );

  case 'sdk-compatibility':
  return (
    <div className="max-w-4xl">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">SDK Compatibility</h1>
      <p className="text-xl text-gray-600 mb-8">
        ModelPool API is compatible with OpenAI SDK and other popular AI libraries. Migrate your existing code with minimal changes.
      </p>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">OpenAI SDK Compatibility</h2>
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <p className="text-green-800 mb-4">
            <strong>100% Compatible:</strong> Simply change the base URL and API key to start using ModelPool with your existing OpenAI SDK code.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h3 className="font-semibold text-green-900 mb-2">✅ Supported Features</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Chat Completions</li>
                <li>• Streaming responses</li>
                <li>• Function calling</li>
                <li>• Vision analysis</li>
                <li>• Embeddings</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h3 className="font-semibold text-green-900 mb-2">🔄 Easy Migration</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Change base URL only</li>
                <li>• Same request/response format</li>
                <li>• All parameters supported</li>
                <li>• Error handling compatible</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">JavaScript/TypeScript SDK</h2>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Installation</h3>
        <CodeBlock
          title="Install OpenAI SDK"
          language="bash"
          id="js-install"
          code={`npm install openai`}
        />

        <h3 className="text-lg font-semibold text-gray-900 mb-3">Configuration</h3>
        <CodeBlock
          title="JavaScript Configuration"
          language="javascript"
          id="js-config"
          code={`import OpenAI from 'openai';

const client = new OpenAI({
  baseURL: 'https://p.qmtju.com/v1',
  apiKey: 'mp_live_your_api_key_here',
});

// Chat completion
const completion = await client.chat.completions.create({
  model: 'DeepSeek-V3',
  messages: [
    { role: 'user', content: 'Hello, how are you?' }
  ],
});

console.log(completion.choices[0].message.content);`}
        />

        <h3 className="text-lg font-semibold text-gray-900 mb-3">Streaming Response</h3>
        <CodeBlock
          title="Streaming Chat"
          language="javascript"
          id="js-streaming"
          code={`const stream = await client.chat.completions.create({
  model: 'DeepSeek-V3',
  messages: [
    { role: 'user', content: 'Write a short story about AI' }
  ],
  stream: true,
});

for await (const chunk of stream) {
  const content = chunk.choices[0]?.delta?.content || '';
  process.stdout.write(content);
}`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Python SDK</h2>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Installation</h3>
        <CodeBlock
          title="Install OpenAI Python SDK"
          language="bash"
          id="python-install"
          code={`pip install openai`}
        />

        <h3 className="text-lg font-semibold text-gray-900 mb-3">Configuration</h3>
        <CodeBlock
          title="Python Configuration"
          language="python"
          id="python-config"
          code={`from openai import OpenAI

client = OpenAI(
    base_url="https://p.qmtju.com/v1",
    api_key="mp_live_your_api_key_here"
)

# Chat completion
completion = client.chat.completions.create(
    model="DeepSeek-V3",
    messages=[
        {"role": "user", "content": "Hello, how are you?"}
    ]
)

print(completion.choices[0].message.content)`}
        />

        <h3 className="text-lg font-semibold text-gray-900 mb-3">Async Client</h3>
        <CodeBlock
          title="Async Python Client"
          language="python"
          id="python-async"
          code={`import asyncio
from openai import AsyncOpenAI

async def main():
    client = AsyncOpenAI(
        base_url="https://p.qmtju.com/v1",
        api_key="mp_live_your_api_key_here"
    )
    
    completion = await client.chat.completions.create(
        model="GLM-4_5",
        messages=[
            {"role": "user", "content": "Explain quantum computing"}
        ]
    )
    
    print(completion.choices[0].message.content)

asyncio.run(main())`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Other Languages</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Go</h3>
            <CodeBlock
              title="Go Example"
              language="go"
              id="go-example"
              code={`package main

import (
    "context"
    "fmt"
    "github.com/sashabaranov/go-openai"
)

func main() {
    config := openai.DefaultConfig("mp_live_your_api_key_here")
    config.BaseURL = "https://p.qmtju.com/v1"
    
    client := openai.NewClientWithConfig(config)
    
    resp, err := client.CreateChatCompletion(
        context.Background(),
        openai.ChatCompletionRequest{
            Model: "DeepSeek-V3",
            Messages: []openai.ChatCompletionMessage{
                {
                    Role:    openai.ChatMessageRoleUser,
                    Content: "Hello!",
                },
            },
        },
    )
    
    if err != nil {
        fmt.Printf("Error: %v\n", err)
        return
    }
    
    fmt.Println(resp.Choices[0].Message.Content)
}`}
            />
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">PHP</h3>
            <CodeBlock
              title="PHP Example"
              language="php"
              id="php-example"
              code={`<?php
require 'vendor/autoload.php';

use OpenAI\\Client;

$client = OpenAI::factory()
    ->withApiKey('mp_live_your_api_key_here')
    ->withBaseUri('https://p.qmtju.com/v1')
    ->make();

$response = $client->chat()->create([
    'model' => 'DeepSeek-V3',
    'messages' => [
        ['role' => 'user', 'content' => 'Hello!'],
    ],
]);

echo $response->choices[0]->message->content;
?>`}
            />
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Framework Integration</h2>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-3">LangChain Integration</h3>
        <CodeBlock
          title="LangChain with ModelPool"
          language="python"
          id="langchain-example"
          code={`from langchain.chat_models import ChatOpenAI
from langchain.schema import HumanMessage

# Initialize with ModelPool endpoint
llm = ChatOpenAI(
    model_name="DeepSeek-V3",
    openai_api_base="https://p.qmtju.com/v1",
    openai_api_key="mp_live_your_api_key_here"
)

# Use with LangChain
messages = [HumanMessage(content="What is machine learning?")]
response = llm(messages)
print(response.content)`}
        />

        <h3 className="text-lg font-semibold text-gray-900 mb-3">LlamaIndex Integration</h3>
        <CodeBlock
          title="LlamaIndex with ModelPool"
          language="python"
          id="llamaindex-example"
          code={`from llama_index.llms import OpenAI
from llama_index import ServiceContext, VectorStoreIndex

# Configure LLM
llm = OpenAI(
    model="GLM-4_5",
    api_base="https://p.qmtju.com/v1",
    api_key="mp_live_your_api_key_here"
)

# Create service context
service_context = ServiceContext.from_defaults(llm=llm)

# Use with LlamaIndex
index = VectorStoreIndex.from_documents(
    documents,
    service_context=service_context
)

query_engine = index.as_query_engine()
response = query_engine.query("Your question here")
print(response)`}
        />
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Migration Guide</h2>
        
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">From OpenAI to ModelPool</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Step 1: Update Configuration</h4>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-red-600 mb-2">❌ Before (OpenAI)</p>
                    <code className="text-xs bg-red-50 text-red-800 p-2 rounded block">
                      baseURL: "https://api.openai.com/v1"<br/>
                      apiKey: "sk-..."
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-600 mb-2">✅ After (ModelPool)</p>
                    <code className="text-xs bg-green-50 text-green-800 p-2 rounded block">
                      baseURL: "https://p.qmtju.com/v1"<br/>
                      apiKey: "mp_live_..."
                    </code>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-blue-900 mb-2">Step 2: Update Model Names</h4>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-red-600 mb-2">❌ OpenAI Models</p>
                    <code className="text-xs bg-red-50 text-red-800 p-2 rounded block">
                      gpt-4<br/>
                      gpt-3.5-turbo<br/>
                      text-embedding-ada-002
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-600 mb-2">✅ ModelPool Models</p>
                    <code className="text-xs bg-green-50 text-green-800 p-2 rounded block">
                      DeepSeek-V3<br/>
                      GLM-4_5<br/>
                      text-embedding-3-large
                    </code>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-blue-900 mb-2">Step 3: Test Your Integration</h4>
              <p className="text-sm text-blue-700">
                All other code remains the same! Request/response formats, streaming, function calling, and error handling work identically.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">🔗 SDK Integration Benefits</h3>
        <ul className="space-y-2 text-purple-800">
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Zero code changes required for most applications
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Access 100+ models with the same familiar interface
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Support for all popular AI frameworks and libraries
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Consistent error handling and response formats
          </li>
          <li className="flex items-start">
            <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Easy A/B testing between different model providers
          </li>
        </ul>
      </div>
    </div>
  );

      default:
        return (
          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Documentation</h1>
            <p className="text-xl text-gray-600 mb-8">
              Please select a section from the navigation menu to view the documentation.
            </p>
          </div>
        );
    }
  };

  const filteredSections = navigationSections.map(section => ({
    ...section,
    items: section.items.filter(item =>
      searchTerm === '' ||
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  return (
    <div>
      <Navbar />
      <div className="flex min-h-screen bg-gray-50">
        {/* 左侧导航 */}
        <div className="w-64 bg-white border-r border-gray-200 flex-shrink-0">
          <div className="sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
            {/* 文档标题 */}
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-900 mb-1">API Documentation</h2>
              <span className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                v1.7
              </span>
            </div>

            {/* 导航菜单 */}
            <nav className="p-3">
              {filteredSections.map((section) => (
                <div key={section.title} className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                    {section.title}
                  </h3>
                  <ul className="space-y-0.5">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={item.id}>
                          <button
                            className={`w-full flex items-center space-x-2 px-2 py-1.5 text-xs rounded-lg transition-colors text-left ${activeSection === item.id
                              ? 'bg-blue-50 text-blue-700 font-medium border-l-2 border-blue-500'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            onClick={() => setActiveSection(item.id)}
                          >
                            <Icon className="h-3 w-3 flex-shrink-0" />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* 右侧内容区域 */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-none mx-auto p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;